import { createContext, useContext, useMemo } from 'react'

import { useEmpreendimentoContext } from '@/features/simulador/brasil/context/empreendimento.context'
import type { Empreendimento } from '@/features/simulador/brasil/domain/empreendimento'
import type { TTipoProdutoPrecificadoBrasilEnum } from '@/types/produto'

export type Simulacao = NonNullable<ReturnType<Empreendimento['getSimulacao']>>

/**
 * Contexto da Simulação
 *
 * Fornece acesso à instância de Simulação (criada sob demanda) e
 * informações de navegação.
 *
 * Responsabilidades:
 * - simulacao: Instância de ISimulacao com métodos de cálculo
 * - indexSimulacao: Índice da simulação no empreendimento
 * - indexEmpreendimento: Índice do empreendimento no formulário
 *
 * NÃO gerencia múltiplas simulações - essa responsabilidade é do EmpreendimentoProvider.
 */
interface ISimulacaoContextValue {
  // Instância da Simulação (criada sob demanda)
  simulacao: Simulacao
  tipoDoProduto: TTipoProdutoPrecificadoBrasilEnum
  // Índices para navegação
  indexSimulacao: number
  indexEmpreendimento: number
}

interface ISimulacaoProviderProps {
  children: React.ReactNode
  indexSimulacao: number
}

const SimulacaoContext = createContext<ISimulacaoContextValue | undefined>(undefined)

export const useSimulacaoContext = <TSimulacao = Simulacao,>() => {
  const context = useContext(SimulacaoContext)

  if (!context) {
    throw new Error('useSimulacaoContext deve ser usado dentro de SimulacaoProvider')
  }

  return {
    ...context,
    simulacao: context.simulacao as TSimulacao,
  }
}

export const SimulacaoProvider = ({ children, indexSimulacao }: ISimulacaoProviderProps) => {
  const { empreendimento, indexEmpreendimento } = useEmpreendimentoContext()

  const simulacao = useMemo(() => {
    const simulacaoEmpreendimento = empreendimento.getSimulacao(indexSimulacao)
    if (!simulacaoEmpreendimento) {
      throw new Error(`Simulação ${indexSimulacao} não encontrada no empreendimento ${empreendimento.idExterno}`)
    }
    return simulacaoEmpreendimento
  }, [empreendimento, indexSimulacao])

  const value = useMemo(
    () => ({
      tipoDoProduto: empreendimento.tipoDoProduto,
      simulacao,
      indexSimulacao,
      indexEmpreendimento,
    }),
    [simulacao, indexSimulacao, indexEmpreendimento],
  )

  return <SimulacaoContext.Provider value={value}>{children}</SimulacaoContext.Provider>
}
